import React, { useMemo, useState } from 'react';
import './JsOutput.css';
import { buildJsExport, defaultFormatOptions } from '../../utils/exportJs';

/** =========================
 *  COMPONENTE
 *  ======================== */
const JsOutput = ({
  lightPoints = [],
  exportName = 'iluminacaoConfig',
  formatOptions = defaultFormatOptions
}) => {
  const [copyStatus, setCopyStatus] = useState('copy');

  // estatísticas
  const stats = useMemo(() => {
    const totalGroups = lightPoints.length;
    const totalInstances = lightPoints.reduce((sum, group) => {
      return sum + (group.instances ? group.instances.length : 0);
    }, 0);

    const moduleCount = {};
    lightPoints.forEach((group) => {
      const key = `M${group.modulo}`;
      moduleCount[key] =
        (moduleCount[key] || 0) +
        (group.instances ? group.instances.length : 0);
    });

    return { totalGroups, totalInstances, moduleCount };
  }, [lightPoints]);

  // gera JS (export) e JSON (apenas para validação)
  const { jsString, jsonString } = useMemo(() => {
    if (!lightPoints?.length) return { jsString: '', jsonString: '' };

    const { jsString, jsonBody } = buildJsExport(
      lightPoints,
      exportName,
      formatOptions
    );

    return { jsString, jsonString: jsonBody };
  }, [lightPoints, exportName, formatOptions]);

  const validation = useMemo(() => {
    if (!jsonString) return { valid: false, error: 'Nenhum conteúdo' };
    try {
      JSON.parse(jsonString);
      return { valid: true, error: null };
    } catch (err) {
      return { valid: false, error: err.message };
    }
  }, [jsonString]);

  const copyToClipboard = async () => {
    if (!jsString) {
      alert('Não há código para copiar!');
      return;
    }
    try {
      await navigator.clipboard.writeText(jsString);
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('copy'), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = jsString;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('copy'), 2000);
    }
  };

  const downloadJs = () => {
    if (!jsString) {
      alert('Não há código para baixar!');
      return;
    }
    const blob = new Blob([jsString], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `${exportName}_${date}.js`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="json-output-container">
      {/* Header */}
      <div className="json-header">
        <h3>📄 Código JS (export)</h3>
      </div>

      {/* Toolbar */}
      <div className="json-toolbar">
        <button
          className={`toolbar-btn copy-btn ${copyStatus}`}
          onClick={copyToClipboard}
          disabled={!jsString}
          title="Copiar código JS"
        >
          {copyStatus === 'copy' ? '📋 Copiar' : '✅ Copiado!'}
        </button>

        <button
          className="toolbar-btn download-btn"
          onClick={downloadJs}
          disabled={!jsString}
          title="Baixar arquivo .js"
        >
          💾 Baixar .js
        </button>
      </div>

      {/* Conteúdo JS */}
      <div className="json-content">
        {jsString ? (
          <pre className="json-code">
            <code>{jsString}</code>
          </pre>
        ) : (
          <div className="json-placeholder">
            <div className="placeholder-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path
                  d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" fill="none" />
                <path d="M16 13H8" stroke="currentColor" strokeWidth="2" />
                <path d="M16 17H8" stroke="currentColor" strokeWidth="2" />
                <path d="M10 9H8" stroke="currentColor" strokeWidth="2" />
              </svg>
              <h4>Nenhum ponto adicionado</h4>
              <p>Adicione pontos de luz na planta para gerar o código JS exportável</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JsOutput;